import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { Box, Button, Box as MuiBox, Fade, FormControlLabel, Switch, Typography } from '@mui/material';

// Your Mapbox access token
mapboxgl.accessToken = 'pk.eyJ1IjoibmluamFjb2NvYSIsImEiOiJjbWR3NndkczcxdDB3MmpvZGVqdjd1enRkIn0.s6Nr-2-Xggdbk5nPwyr4UQ';

interface MapProps {
  lat: number;
  lng: number;
  zoom: number;
  address: string;
}

const Map: React.FC<MapProps> = ({ lat, lng, zoom }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [style, setStyle] = useState<'streets' | 'satellite'>('streets');
  const [isInteractionEnabled, setIsInteractionEnabled] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (mapContainer.current) {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: style === 'streets'
          ? 'mapbox://styles/mapbox/streets-v12'
          : 'mapbox://styles/mapbox/satellite-streets-v12',
        center: [lng, lat],
        zoom: zoom,
        interactive: isInteractionEnabled
      });

      // Add navigation controls
      map.current.addControl(new mapboxgl.NavigationControl());

      // Add marker
      new mapboxgl.Marker()
        .setLngLat([lng, lat])
        .addTo(map.current);
    }

    return () => {
      if (map.current) {
        map.current.remove();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng, zoom, style]);

  // Toggle interaction handlers when switch changes
  useEffect(() => {
    if (!map.current) return;

    const handlers = [
      'scrollZoom',
      'boxZoom',
      'dragRotate',
      'dragPan',
      'keyboard',
      'doubleClickZoom',
      'touchZoomRotate'
    ];

    handlers.forEach(handler => {
      // @ts-ignore
      const h = map.current[handler];
      if (h) {
        if (isInteractionEnabled) {
          h.enable();
        } else {
          h.disable();
        }
      }
    });
  }, [isInteractionEnabled]);

  return (
    <MuiBox 
      sx={{ position: 'relative', width: '100%', height: '100%' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box 
        ref={mapContainer}
        sx={{ 
          width: '100%', 
          height: '100%',
          '& .mapboxgl-canvas': {
            width: '100% !important',
            height: '100% !important',
            outline: 'none'
          }
        }}
      />
      
      <Fade in={isInteractionEnabled}>
        <Button
          variant="contained"
          size="small"
          sx={{ 
            position: 'absolute', 
            top: 8, 
            right: 8, 
            zIndex: 10, 
            backgroundColor: 'rgba(255,255,255,0.85)', 
            color: '#333', 
            minWidth: 0, 
            px: 1.5, 
            py: 0.5 
          }}
          onClick={() => setStyle(style === 'streets' ? 'satellite' : 'streets')}
        >
          {style === 'streets' ? 'Terrain' : 'Streets'}
        </Button>
      </Fade>

      <Fade in={isHovered || isInteractionEnabled}>
        <Box sx={{ 
          position: 'absolute', 
          bottom: 20, 
          left: 20, 
          zIndex: 10, 
          backgroundColor: 'rgba(255,255,255,0.9)', 
          borderRadius: 1, 
          px: 1.5, 
          py: 0.5,
          boxShadow: 1,
          display: 'flex',
          alignItems: 'center'
        }}>
          <FormControlLabel
            control={
              <Switch 
                size="small"
                checked={isInteractionEnabled} 
                onChange={(e) => setIsInteractionEnabled(e.target.checked)} 
              />
            }
            label={<Typography variant="body2" sx={{ fontWeight: 500, userSelect: 'none' }}>Enable Map</Typography>}
            sx={{ mr: 0, ml: 0 }}
          />
        </Box>
      </Fade>
    </MuiBox>
  );
};

export default Map;
